import i18next from "../i18n/i18nInit"
import type { CreateProjectComparisonCommand } from "../models/models"
import { bundleDtoToBundle } from "../redux/bosatlas/bosatlas.actions"
import type { Bundle } from "../redux/bosatlas/bosatlas.types"
import type { Comparison } from "../redux/comparisons/comparison.types"
import type { HttpResponse } from "../redux/http/http.types"
import type { IMapDrawing } from "../redux/projects/projects.types"
import type { Project } from "../redux/projects/projects.types"
import { dispatchSnackbar } from "../services/SnackbarService"
import { hasFailed } from "../utils"
import type {
  ComparisonLayout,
  ComparisonProjectDto,
  CopyProjectComparisonCommand,
  ProjectComparisonDetailDto,
  ProjectComparisonProjectDto,
  UpdateProjectComparisonCommand
} from "./../models/models"
import { get, post, put, remove } from "./HttpService"

export async function createProjectComparison(projectComparison: Comparison, brandName: string): Promise<string> {
  const command: CreateProjectComparisonCommand = {
    title: projectComparison.title,
    projects: projectComparison.projects.map(createComparisonProject),
    bundleIds: projectComparison.bundles.map((bundle: Bundle) => bundle.id!),
    layout: projectComparison.layout as ComparisonLayout
  }

  const response: HttpResponse<string> = await post<string, CreateProjectComparisonCommand>(
    "projectComparison",
    "projectcomparisons",
    command
  )

  dispatchSnackbar(
    i18next.t("general:comparison"),
    "POST",
    !hasFailed(response),
    i18next.t("snackbar:savedInAtlas", {
      title: projectComparison.title,
      brandName
    })
  )

  return response.data!
}

export async function updateProjectComparison(projectComparison: Comparison): Promise<void> {
  if (!projectComparison.id) return

  const command: UpdateProjectComparisonCommand = {
    id: projectComparison.id,
    title: projectComparison.title,
    projects: projectComparison.projects.map(createComparisonProject),
    bundleIds: projectComparison.bundles.map((bundle: Bundle) => bundle.id!),
    layout: projectComparison.layout as ComparisonLayout
  }

  const response: HttpResponse = await put<UpdateProjectComparisonCommand>(
    "projectComparison",
    `projectcomparisons/${projectComparison.id}`,
    command
  )

  dispatchSnackbar(i18next.t("general:comparison"), "PUT", !hasFailed(response))
}

export async function removeProjectComparison(id: string): Promise<void> {
  const response: HttpResponse = await remove("projectcomparison", `projectcomparisons/${id}`)

  dispatchSnackbar(i18next.t("general:comparison"), "DELETE", !hasFailed(response))
}

export async function copyProjectComparison(comparison: Comparison, brandName: string): Promise<string> {
  const command: CopyProjectComparisonCommand = {
    comparisonId: comparison.id as string,
    title: comparison.title,
    projects: comparison.projects.map(createComparisonProject)
  }

  const response: HttpResponse<string> = await post<string, CopyProjectComparisonCommand>(
    "projectcomparison",
    "projectcomparisons/copy",
    command
  )

  dispatchSnackbar(
    i18next.t("general:comparison"),
    "POST",
    !hasFailed(response, true),
    i18next.t("snackbar:savedInAtlas", {
      title: comparison.title,
      brandName
    })
  )

  return response.data!
}

export async function getProjectComparison(projectComparisonId: string): Promise<Comparison> {
  const response: HttpResponse<ProjectComparisonDetailDto> = await get<ProjectComparisonDetailDto>(
    "projectComparison",
    `projectcomparisons/${projectComparisonId}`
  )
  const comparison: ProjectComparisonDetailDto = response.data!

  return projectComparisonDetailDtoToProjectComparison(comparison)
}

function createComparisonProject(project: Project): ComparisonProjectDto {
  return {
    mapId: project.id ? undefined : project.metaDataId,
    projectId: project.id,
    drawing: stringifyDrawings(project.drawings),
    projectTitle: project.title
  }
}

function stringifyDrawings(drawings: IMapDrawing[]): string {
  return JSON.stringify(drawings)
}

function projectComparisonDetailDtoToProjectComparison(dto: ProjectComparisonDetailDto): Comparison {
  const projects: Project[] = []

  projects.push(comparisonProjectDtoToProject(dto.first as ProjectComparisonProjectDto))
  projects.push(comparisonProjectDtoToProject(dto.second as ProjectComparisonProjectDto))
  if (dto.third) {
    projects.push(comparisonProjectDtoToProject(dto.third))
  }
  if (dto.fourth) {
    projects.push(comparisonProjectDtoToProject(dto.fourth))
  }

  return {
    id: dto.id,
    title: dto.title,
    projects,
    bundles: dto.bundles ? dto.bundles.map(bundleDtoToBundle) : [],
    layout: dto.layout,
    profileId: dto.profileId
  }
}

function comparisonProjectDtoToProject(dto: ProjectComparisonProjectDto): Project {
  return {
    id: dto.id,
    title: dto.title,
    metaDataId: dto.metadataId,
    drawings: JSON.parse(dto.drawing as string),
    attachments: [],
    bundles: []
  }
}
